import { GOOGLE_PAY_CC_KEY, APPLE_PAY_CC_KEY } from '@pano-project/utils/getCcMethodDisplayTitle';
import { getPageOrigin } from './helpers';

export interface IDigitalWalletDeviceSupport {
  google_pay: boolean;
  apple_pay: boolean;
}

// PRX-23109 (T4.30): tri-state per-wallet result of this pre-check, as
// surfaced to Redux (see filterUnsupportedDigitalWalletGateways below and
// fetchGateways in state/actions/index.js). `null` means this pre-check
// never actually ran *for that wallet* -- either neither wallet had a
// separate google_pay_cc/apple_pay_cc tile in the fetched gateway list, or
// only the other wallet did -- and must be treated as permissive
// (unknown), exactly like before this signal existed. `true`/`false` is
// this pre-check's own confirmed, per-wallet result. This is intentionally
// a *different* type from IDigitalWalletDeviceSupport above: that one is
// the raw, non-nullable SDK response for a check that actually ran.
export interface IDigitalWalletDeviceCompatibility {
  google_pay: boolean | null;
  apple_pay: boolean | null;
}

// PRX-23898 (user-requested): a single, shared predicate for "is this
// payment method a digital wallet (Google Pay / Apple Pay)", so that check
// doesn't get hardcoded independently in every consumer that needs it
// (this file's own two inline checks below, old-theme gateway filtering in
// fetchGateways, and any future one). Takes the whole payment method object,
// not just its key, so a future dedicated `is_digital_wallet: true/false`
// backend field could be checked here too without changing any call site.
export const isDigitalWallet = (
  paymentMethod?: { payment_method_key?: string | null } | null,
): boolean =>
  paymentMethod?.payment_method_key === GOOGLE_PAY_CC_KEY ||
  paymentMethod?.payment_method_key === APPLE_PAY_CC_KEY;

const NOT_SUPPORTED: IDigitalWalletDeviceSupport = { google_pay: false, apple_pay: false };

// A capability-check-only PraxisGate instance is reused for the lifetime of
// the page rather than reconstructed on every gateway-list fetch.
let cachedCompatibilityGate: any = null;

// PRX-23109: `window.PraxisGate`
// is set by a separately-loaded `<script>` tag (see
// read-token-from-url.js) that can still be loading by the time this check
// runs -- `fetchGateways` (state/actions/index.js) calls it right after the
// gateway-list REST/GraphQL response resolves, with no guarantee that race
// has already finished. This used to check `typeof window.PraxisGate ===
// 'undefined'` once, synchronously, and return NOT_SUPPORTED for the rest
// of the session if it lost that race -- nothing ever re-checked later.
// Reported as intermittent (roughly 4 times out of 6, depending on how
// fast the gateway-list response came back relative to the SDK script's
// own load time).
//
// `waitForPraxisGate` reuses the exact wait pattern credit-card-form.js's
// "init HPF SDK" effect already established for this same race: poll every
// `PRAXIS_GATE_POLL_INTERVAL_MS`, and/or the SDK script's own
// `PraxisGateLoaded` event, whichever fires first. Unlike that effect
// (which polls indefinitely, bounded only by the component's own
// unmount), this runs inside a one-shot async thunk with nothing else to
// bound it, so it's capped at `PRAXIS_GATE_WAIT_TIMEOUT_MS` -- long enough
// for a normal script load, short enough not to stall the gateway list
// pipeline behind a genuinely-never-loading SDK (blocked script, network
// failure), which still needs to fail closed same as before.
const DEFAULT_PRAXIS_GATE_POLL_INTERVAL_MS = 150;
const DEFAULT_PRAXIS_GATE_WAIT_TIMEOUT_MS = 5000;

// Mutable so tests can shrink these to a few milliseconds instead of
// waiting multiple seconds of real time -- see
// __setPraxisGateWaitConfigForTests below.
let praxisGatePollIntervalMs = DEFAULT_PRAXIS_GATE_POLL_INTERVAL_MS;
let praxisGateWaitTimeoutMs = DEFAULT_PRAXIS_GATE_WAIT_TIMEOUT_MS;

const isPraxisGateDefined = (): boolean =>
  typeof window !== 'undefined' && typeof (window as any).PraxisGate !== 'undefined';

const waitForPraxisGate = (): Promise<boolean> => {
  if (isPraxisGateDefined()) return Promise.resolve(true);
  if (typeof window === 'undefined') return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;

    const finish = (isReady: boolean) => {
      if (settled) return;
      settled = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      window.removeEventListener('PraxisGateLoaded', onLoaded);
      resolve(isReady);
    };

    const onLoaded = () => finish(true);

    const intervalId = setInterval(() => {
      if (isPraxisGateDefined()) finish(true);
    }, praxisGatePollIntervalMs);

    const timeoutId = setTimeout(() => finish(false), praxisGateWaitTimeoutMs);

    window.addEventListener('PraxisGateLoaded', onLoaded);
  });
};

/**
 * Test-only: shrink the poll interval/timeout so tests exercising the wait
 * (PraxisGate not yet defined, or never becoming defined) don't need real
 * multi-second delays. Call with no arguments to restore the defaults.
 */
export const __setPraxisGateWaitConfigForTests = (
  config: { pollIntervalMs?: number; waitTimeoutMs?: number } = {},
) => {
  praxisGatePollIntervalMs = config.pollIntervalMs ?? DEFAULT_PRAXIS_GATE_POLL_INTERVAL_MS;
  praxisGateWaitTimeoutMs = config.waitTimeoutMs ?? DEFAULT_PRAXIS_GATE_WAIT_TIMEOUT_MS;
};

/**
 * PRX-23109: before showing Google Pay / Apple Pay as selectable payment
 * methods in a deposit session, ask the PG HPF SDK whether *this device*
 * actually supports each wallet, via the SDK's `checkDigitalWalletDeviceCompatibility()`
 * function.
 *
 * `is_google_pay_enabled`/`is_apple_pay_enabled` are passed straight through
 * as the SDK call's own arguments (`gate.checkDigitalWalletDeviceCompatibility({
 * is_google_pay_enabled, is_apple_pay_enabled })`), telling it which
 * wallet(s) this caller actually wants checked -- e.g. only the providers
 * that key currently exists in the fetched payment-method list. This is a
 * different mechanism from an earlier version of this function, which
 * tried to communicate the same thing via `transaction_data` on the
 * `PraxisGate` constructor instead of an argument to this call; that
 * approach broke the check on a real device and was reverted. The two
 * flags are also used locally, before calling the SDK at all, to skip the
 * whole check when neither is true (nothing to check). Once the SDK
 * responds, its result is trusted as-is and returned unmodified -- the SDK
 * itself is the one source of truth for whether a wallet it was told about
 * is actually supported, now that it receives the same flags this function
 * does.
 *
 * That SDK function (and the "containers-only" init format used to reach
 * it) is being added to the PG HPF SDK in a separate repo and is not
 * necessarily present on the SDK build this app currently loads. This call
 * is written defensively for that reason: it waits for `window.PraxisGate`
 * (see `waitForPraxisGate` above) and feature-detects the method itself
 * before calling either, and treats "the check couldn't run" the same as
 * "not supported" (fail closed) -- Cashier must not throw, and must not
 * offer a wallet button the device may not actually be able to use.
 */
export const checkDigitalWalletDeviceSupport = async ({
  token,
  is_google_pay_enabled,
  is_apple_pay_enabled,
}: {
  token: string;
  is_google_pay_enabled: boolean;
  is_apple_pay_enabled: boolean;
}): Promise<IDigitalWalletDeviceSupport> => {
  if (
    typeof window === 'undefined' ||
    !token ||
    (!is_google_pay_enabled && !is_apple_pay_enabled)
  ) {
    return NOT_SUPPORTED;
  }

  const isPraxisGateReady = await waitForPraxisGate();
  if (!isPraxisGateReady) {
    return NOT_SUPPORTED;
  }

  try {
    const gate =
      cachedCompatibilityGate ??
      new (window as any).PraxisGate({
        sessionToken: token,
        containers: {},
        page_origin: getPageOrigin(),
      });
    cachedCompatibilityGate = gate;

    if (typeof gate?.checkDigitalWalletDeviceCompatibility !== 'function') {
      return NOT_SUPPORTED;
    }

    const result = await gate.checkDigitalWalletDeviceCompatibility({
      is_google_pay_enabled,
      is_apple_pay_enabled,
    });

    return {
      google_pay: result?.google_pay === true,
      apple_pay: result?.apple_pay === true,
    };
  } catch (error) {
    return NOT_SUPPORTED;
  }
};

/** Test-only: clears the cached capability-check gate instance. */
export const __resetDigitalWalletDeviceCompatibilityCacheForTests = () => {
  cachedCompatibilityGate = null;
};

/**
 * PRX-23109: releases the capability-check-only `PraxisGate` instance
 * once the real payment flow is about to construct its own instance for the
 * actually-selected method (see `pciScript` in credit-card-form.js).
 *
 * `cachedCompatibilityGate` above is deliberately reused across repeated
 * gateway-list fetches, but nothing ever cleared it afterwards -- so it
 * stayed alive for the rest of the page's lifetime, including through the
 * moment the customer actually selected Google Pay/Apple Pay and a second,
 * real `PraxisGate` instance was constructed for that wallet's container.
 * Observed on a real dev server: the real instance's network round trip
 * completes, but its `onTokenizationAvailability` postMessage is never
 * received, so the skeleton loader (`isWaitingForWalletCheck` in
 * credit-card-page.js) never clears.
 *
 * `window.PraxisGate.removeContainerFromParams` was called here in an
 * earlier version of this fix, on the (incorrect) assumption that it was an
 * existing SDK static -- it isn't, and isn't needed: dropping the reference
 * to the cached, containers-only instance below is the actual fix (nothing
 * else in this module holds onto it once it's `null`, so it becomes
 * eligible for garbage collection, and a later capability check constructs
 * a brand-new instance instead of reusing this one).
 */
export const releaseDigitalWalletCompatibilityGate = () => {
  cachedCompatibilityGate = null;
};

/**
 * Filters google_pay_cc/apple_pay_cc out of a mapped payment-method list
 * when the device doesn't support that wallet (or the check couldn't run).
 * Every other gateway (including Credit Card) passes through untouched.
 * A no-op when neither wallet key is present, so it never runs the device
 * check for a session that has no wallet methods to filter.
 *
 * PRX-23109 (T4.30): also returns this check's own per-wallet `support`
 * result (previously computed here and then discarded once the gateway
 * list was filtered) so the caller (fetchGateways in state/actions/index.js)
 * can surface it to Redux -- a *separate*, later, and otherwise
 * unconnected code path (credit-card-form.js's pciScript,
 * DigitalWalletButton.tsx) needs this same per-wallet result to decide
 * whether to even request a wallet's container from the third-party PG HPF
 * SDK for the embedded (toggle-inside-Credit-Card) configuration, instead
 * of only checking whether the merchant enabled it. `support.google_pay`/
 * `support.apple_pay` is `null` (not `false`) whenever that specific
 * wallet had no separate tile in this fetch's own gateway list -- i.e. the
 * check never actually ran *for that wallet* -- so a consumer can tell
 * "confirmed unsupported" apart from "never checked" and stay permissive
 * for the latter, exactly like today.
 */
export const filterUnsupportedDigitalWalletGateways = async <
  T extends { payment_method_key?: string },
>(
  gateways: T[] | null | undefined,
  token: string | undefined,
  // PRX-23109 (user-reported): a wallet's device-compatibility result is a
  // property of the physical device for the rest of this page session --
  // change-pay-info.js's own getDigitalWalletDataByOptions already carries
  // `isDeviceCompatible` forward through every gateway switch for exactly
  // that reason (see its own doc comment). This function itself, though,
  // used to re-run the SDK's own check unconditionally on *every* call --
  // including a currency change's own fetchGateways refetch, which happens
  // long after the customer already selected a wallet and its real,
  // per-container PraxisGate instance (pciScript in credit-card-form.js)
  // is already alive. Because that real instance's own construction
  // releases the cached compatibility-check gate
  // (releaseDigitalWalletCompatibilityGate, called right before it), that
  // "just changed currency" refetch had nothing cached to reuse and built
  // a brand-new, throwaway compatibility-check PraxisGate instance instead
  // -- alive at the same time as the real one, exactly the
  // two-simultaneous-instances situation releaseDigitalWalletCompatibilityGate's
  // own doc comment already identifies as suspected to silently drop the
  // real instance's events. Observed effect: the already-selected,
  // already-supported wallet's tile spuriously vanished from the refetched
  // list this call produces, which fetchGateways' own caller
  // (useGatewayAutoSelection.ts) then reads as "the current gateway is no
  // longer in the list" and silently switches the customer off it. Passing
  // in the confirmed result this session already has (via
  // `knownSupport`, read from gPayData/applePayData.isDeviceCompatible)
  // lets this function skip the SDK check entirely for a wallet it already
  // has a confirmed true/false answer for -- so it never has a reason to
  // construct a second instance once a wallet's compatibility is already
  // settled, no matter how many more times a currency change calls
  // fetchGateways afterwards. A wallet neither checked yet this session
  // (`null`) still gets a real check, exactly as before.
  knownSupport: IDigitalWalletDeviceCompatibility = { google_pay: null, apple_pay: null },
): Promise<{ gateways: T[]; support: IDigitalWalletDeviceCompatibility }> => {
  const list = Array.isArray(gateways) ? gateways : [];

  const hasGooglePay = list.some((gateway) => gateway?.payment_method_key === GOOGLE_PAY_CC_KEY);
  const hasApplePay = list.some((gateway) => gateway?.payment_method_key === APPLE_PAY_CC_KEY);

  if (!hasGooglePay && !hasApplePay) {
    return { gateways: list, support: { google_pay: null, apple_pay: null } };
  }

  const needsGooglePayCheck = hasGooglePay && knownSupport.google_pay === null;
  const needsApplePayCheck = hasApplePay && knownSupport.apple_pay === null;

  const rawSupport =
    needsGooglePayCheck || needsApplePayCheck
      ? await checkDigitalWalletDeviceSupport({
          token,
          is_google_pay_enabled: needsGooglePayCheck,
          is_apple_pay_enabled: needsApplePayCheck,
        })
      : NOT_SUPPORTED;

  const support: IDigitalWalletDeviceCompatibility = {
    google_pay: hasGooglePay
      ? needsGooglePayCheck
        ? rawSupport.google_pay
        : knownSupport.google_pay
      : null,
    apple_pay: hasApplePay
      ? needsApplePayCheck
        ? rawSupport.apple_pay
        : knownSupport.apple_pay
      : null,
  };

  const filteredGateways = list.filter((gateway) => {
    if (gateway?.payment_method_key === GOOGLE_PAY_CC_KEY) return support.google_pay === true;
    if (gateway?.payment_method_key === APPLE_PAY_CC_KEY) return support.apple_pay === true;
    return true;
  });

  return { gateways: filteredGateways, support };
};
